import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { CheckoutQueryFacade, CheckoutState } from '@spartacus/checkout/base/root';
import { OCC_USER_ID_ANONYMOUS, ProcessSelectors, QueryState, StateWithProcess, UserIdService, WindowRef } from '@spartacus/core';
import { Order, OrderFacade, ScheduleReplenishmentForm } from '@spartacus/order/root';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CustomCheckoutActions } from '../store/actions';
import {
  PAYPAL_CREATE_ORDER_PROCESS_ID,
  PAYPAL_LOAD_PAYMENT_PROCESS_ID,
  PLACED_ORDER_PROCESS_ID,
  REDSYS_LOAD_PAYMENT_PROCESS_ID,
  SEQURA_LOAD_PAYMENT_PROCESS_ID,
  StateWithCheckout
} from '../store/custom-checkout-state';

export enum ORDER_TYPE {
  PLACE_ORDER = 'PLACE_ORDER',
  SCHEDULE_REPLENISHMENT_ORDER = 'SCHEDULE_REPLENISHMENT_ORDER'
}

@Injectable({
  providedIn: 'root'
})
export class CustomCheckoutService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  cartId = 'current';
  userId: string;

  constructor(
    protected checkoutStore: Store<StateWithCheckout>,
    protected processStateStore: Store<StateWithProcess<void>>,
    protected activeCartService: ActiveCartFacade,
    protected userIdService: UserIdService,
    protected checkoutQueryFacade: CheckoutQueryFacade,
    protected orderFacade: OrderFacade,
    protected winRef: WindowRef
  ) {}

  getCartID(): void {
    this.activeCartService
      .getActiveCartId()
      .subscribe((response) => {
        return (this.cartId = response);
      })
      .unsubscribe();
  }

  getCartActive(): void {
    this.activeCartService
      .getActive()
      .subscribe((response) => {
        return response;
      })
      .unsubscribe();
  }

  getUserID(): void {
    this.userIdService
      .takeUserId()
      .subscribe((userID) => (this.userId = userID))
      .unsubscribe();
  }

  /**
   * Init Redsys payment
   */
  initRedsysPayment(saveCard: boolean): void {
    this.getUserID();
    this.getCartID();
    if (this.userId && this.cartId) {
      this.checkoutStore.dispatch(
        new CustomCheckoutActions.LoadRedsysPayment({
          userId: this.userId,
          cartId: this.cartId,
          saveCard
        })
      );
    }
  }

  clearRedsysPaymentState(): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearLoadRedsysPayment());
  }

  /**
   * Returns the load Redsys payment loading flag
   */
  getRedsysPaymentLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(REDSYS_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Redsys payment success flag
   */
  getRedsysPaymentSuccess(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessSuccessFactory(REDSYS_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Redsys payment state
   */
  getRedsysPaymentState(): Observable<any> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessStateFactory(REDSYS_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Redsys payment error flag
   */
  getRedsysPaymentError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(REDSYS_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Init Sequra payment
   */
  initSequraPayment(paymentId: string): void {
    this.getUserID();
    this.getCartID();
    if (this.userId && this.cartId) {
      this.checkoutStore.dispatch(
        new CustomCheckoutActions.LoadSequraPayment({
          userId: this.userId,
          cartId: this.cartId,
          paymentId
        })
      );
    }
  }

  clearSequraPaymentState(): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearLoadSequraPayment());
  }

  /**
   * Returns the load Sequra payment loading flag
   */
  getSequraPaymentLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(SEQURA_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Sequra payment success flag
   */
  getSequraPaymentSuccess(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessSuccessFactory(SEQURA_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Sequra payment state
   */
  getSequraPaymentState(): Observable<any> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessStateFactory(SEQURA_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Sequra payment error flag
   */
  getSequraPaymentError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(SEQURA_LOAD_PAYMENT_PROCESS_ID)));
  }

  createPaypalOrder(paymentId: string, token: string, PayerID: string, cartId: string): void {
    this.getCartActive();
    this.getUserID();
    if (this.userId && this.cartId) {
      this.checkoutStore.dispatch(
        new CustomCheckoutActions.CreatePaypalOrder({
          userId: this.userId,
          cartId,
          paymentId,
          token,
          PayerID
        })
      );
    }
  }

  clearPaypalPaymentState(): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearPaypalSequraPayment());
  }

  /**
   * Returns the load Paypal payment loading flag
   */
  getPaypalOrderLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(PAYPAL_CREATE_ORDER_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment success flag
   */
  getPaypalOrderSuccess(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessSuccessFactory(PAYPAL_CREATE_ORDER_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment state
   */
  getPaypalOrderState(): Observable<any> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessStateFactory(PAYPAL_CREATE_ORDER_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment error flag
   */
  getPaypalOrderError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(PAYPAL_CREATE_ORDER_PROCESS_ID)));
  }

  initPaypalPayment(paymentId: string): void {
    this.getUserID();
    this.getCartID();
    if (this.userId && this.cartId) {
      this.checkoutStore.dispatch(
        new CustomCheckoutActions.LoadPaypalPayment({
          userId: this.userId,
          cartId: this.cartId,
          paymentId
        })
      );
    }
  }

  /**
   * Returns the load Paypal payment loading flag
   */
  getPaypalPaymentLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(PAYPAL_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment success flag
   */
  getPaypalPaymentSuccess(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessSuccessFactory(PAYPAL_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment state
   */
  getPaypalPaymentState(): Observable<any> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessStateFactory(PAYPAL_LOAD_PAYMENT_PROCESS_ID)));
  }

  /**
   * Returns the load Paypal payment error flag
   */
  getPaypalPaymentError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(PAYPAL_LOAD_PAYMENT_PROCESS_ID)));
  }

  placeOrder(termsChecked: boolean): void {
    if (this.actionAllowed()) {
      let userId;
      this.userIdService
        .getUserId()
        .subscribe((occUserId) => (userId = occUserId))
        .unsubscribe();

      let cartId;
      this.activeCartService
        .getActiveCartId()
        .subscribe((activeCartId) => (cartId = activeCartId))
        .unsubscribe();

      if (userId && cartId) {
        this.checkoutStore.dispatch(
          new CustomCheckoutActions.PlaceOrder({
            userId,
            cartId,
            termsChecked
          })
        );
      }
    }
  }

  /**
   * Load checkout details data
   * @param cartId : string Cart ID of loaded cart
   */
  loadCheckoutState(cartId: string) {
    let userId;
    this.userIdService
      .getUserId()
      .subscribe((occUserId) => (userId = occUserId))
      .unsubscribe();
    if (userId) {
      this.checkoutStore.dispatch(
        new CustomCheckoutActions.LoadCheckoutState({
          userId,
          cartId
        })
      );
    }
  }

  /**
   * Trigger the event when the user starts the checkout flow
   */
  onCheckoutBeginEvent() {
    this.activeCartService
      .getActive()
      .subscribe((cart) => this.checkoutStore.dispatch(new CustomCheckoutActions.OnCheckoutBegin({ cart: cart })))
      .unsubscribe();
  }

  /**
   * Trigger the event when the user has selected a shipping method during the checkout process
   */
  addShippingInfoEvent(): void {
    this.activeCartService
      .getActive()
      .subscribe((cart) => this.checkoutStore.dispatch(new CustomCheckoutActions.AddShippingInfoSuccess({ cart: cart })))
      .unsubscribe();
  }

  /**
   * Trigger the event when the user has selected a payment method during the checkout process
   */
  addPaymentInfoEvent(): void {
    this.activeCartService
      .getActive()
      .subscribe((cart) => this.checkoutStore.dispatch(new CustomCheckoutActions.AddPaymentInfoSuccess({ cart: cart })))
      .unsubscribe();
  }

  /**
   * Trigger the event when the order confirmation page is loaded
   */
  loadOrderConfirmationSuccess(order: Order): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.OnPurchaseConfirmationSuccess({ order: order }));
  }

  /**
   * Resets the place or schedule replenishment order's processing state
   */
  clearPlaceOrderState(): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearPlaceOrder());
  }

  /**
   * Clear checkout data
   */
  clearCheckoutData(): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearCheckoutData());
  }

  /**
   * Set checkout order type
   * @param orderType : an enum of types of order we are placing
   */
  setOrderType(orderType: ORDER_TYPE): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.SetOrderType(orderType));
  }

  /**
   * Schedule a replenishment order
   */
  scheduleReplenishmentOrder(scheduleReplenishmentForm: ScheduleReplenishmentForm, termsChecked: boolean): void {
    let cartId: string;

    this.subscriptions.add(
      this.activeCartService
        .getActiveCartId()
        .pipe(take(1))
        .subscribe((activeCartId) => (cartId = activeCartId))
    );

    this.userIdService.takeUserId(false).subscribe(
      (userId) => {
        if (Boolean(cartId) && Boolean(userId)) {
          this.checkoutStore.dispatch(
            new CustomCheckoutActions.ScheduleReplenishmentOrder({
              cartId,
              scheduleReplenishmentForm,
              termsChecked,
              userId
            })
          );
        }
      },
      () => {
        // TODO: for future releases, refactor this part to thrown errors
      }
    );
  }

  /**
   * Returns the place or schedule replenishment order's loading flag
   */
  getPlaceOrderLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(PLACED_ORDER_PROCESS_ID)));
  }

  /**
   * Returns the place or schedule replenishment order's success flag
   */
  getPlaceOrderSuccess(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessSuccessFactory(PLACED_ORDER_PROCESS_ID)));
  }

  /**
   * Returns the place or schedule replenishment order's error flag
   */
  getPlaceOrderError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(PLACED_ORDER_PROCESS_ID)));
  }

  protected actionAllowed(): boolean | Observable<boolean> {
    let userId;
    this.userIdService
      .getUserId()
      .subscribe((occUserId) => (userId = occUserId))
      .unsubscribe();
    return (userId && userId !== OCC_USER_ID_ANONYMOUS) || this.activeCartService.isGuestCart();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * Clear checkout step
   * @param stepNumber : the step number to be cleared
   */
  clearCheckoutStep(stepNumber: number): void {
    this.checkoutStore.dispatch(new CustomCheckoutActions.ClearCheckoutStep(stepNumber));
  }

  /**
   * Get status of checkout details loaded
   */
  getCheckoutDetailsLoaded(): Observable<QueryState<CheckoutState | undefined>> {
    return this.checkoutQueryFacade.getCheckoutDetailsState();
  }

  /**
   * Get order details
   */
  getOrderDetails(): Observable<Order | undefined> {
    return this.orderFacade.getOrderDetails();
  }
}
